import clsx from 'clsx'
import styles from './ProductVotes.module.css'
import React, { CSSProperties, useEffect, useState } from 'react'
import {
  BasicStyledComponent,
  DSProductLikesShort,
  DSSubmitProductVoteResult,
  DSVote,
} from 'shared-definitions/types'
import LineRatio from './LineRatio'
import ButtonReaction from '../buttons/ButtonReaction'
import { useProductVotes } from '../hooks/use-product-votes'

export interface BestListComparisonTableCelVoteProps extends BasicStyledComponent {
  style?: CSSProperties
  variant: 'compact' | 'full'
  data: DSProductLikesShort
  vote: (productId: string, vote: DSVote) => Promise<DSSubmitProductVoteResult | null>
}

const ProductVotes: React.FC<BestListComparisonTableCelVoteProps> = ({
  style,
  className,
  data,
  vote,
  variant,
}) => {
  const [pending, setPending] = useState(true)
  const {
    votesLoaded,
    setVoteInfo,
    voteInfo: { userVote, votesCount },
  } = useProductVotes({
    backendVotes: data,
    productId: data.id,
  })

  useEffect(() => {
    if (votesLoaded) {
      setPending(false)
    }
  }, [votesLoaded])

  return (
    <div
      style={style}
      className={clsx(styles.container, className, {
        [styles.pending]: pending,
        [styles.hasVote]: userVote !== null,
        [styles.compact]: variant === 'compact',
        [styles.full]: variant === 'full',
      })}
    >
      <ButtonReaction
        variant="like"
        disabled={userVote !== null && userVote !== 'like'}
        selected={userVote === 'like'}
        className={clsx(styles.button, styles.likeButton)}
        onClick={() => {
          if (userVote) {
            return
          }
          setPending(true)

          void vote(data.id, 'like')
            .then(result => {
              if (result) {
                setVoteInfo({
                  userVote: 'like',
                  votesCount: result,
                })
              }
            })
            .finally(() => {
              setPending(false)
            })
        }}
      />
      <ButtonReaction
        variant="dislike"
        disabled={userVote !== null && userVote !== 'dislike'}
        selected={userVote === 'dislike'}
        className={clsx(styles.button, styles.dislikeButton)}
        onClick={() => {
          if (userVote) {
            return
          }
          setPending(true)

          void vote(data.id, 'dislike')
            .then(result => {
              if (result) {
                setVoteInfo({
                  userVote: 'dislike',
                  votesCount: result,
                })
              }
            })
            .finally(() => {
              setPending(false)
            })
        }}
      />
      <LineRatio
        left={votesCount.likes}
        right={votesCount.dislikes}
        className={styles.lineAlt}
        fgClassName={styles.lineAltFg}
      />
      <div className={styles.hint}>{`${votesCount.likes + votesCount.dislikes} total votes`}</div>
    </div>
  )
}

export default ProductVotes
